/**
 * ApprovalInsightsBadge — unified risk + optional L3 approval history badge.
 *
 * When `canonicalRisk` is supplied (access-request spine), L3 memory is not fetched.
 * When `fetchInsights` is supplied, historical approval stats may augment the badge.
 */

const React = (globalThis as { React?: typeof import('react') }).React;
const { useState, useEffect, useMemo } = React!;

import { CdaIcon, type CdaIconName } from '../cda/icons';
import {
  riskLevelToBadgeColor,
  type UnifiedApprovalRisk,
} from '../utils/unifiedApprovalRisk';
import {
  EMPTY_INSIGHTS,
  type ApprovalInsightsResult,
  type FetchApprovalInsights,
} from './approvalInsightsTypes';

export type { ApprovalInsightsResult, ApprovalStats, FetchApprovalInsights } from './approvalInsightsTypes';
export { EMPTY_INSIGHTS } from './approvalInsightsTypes';

const styles = {
  container: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  badge: (color: string) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.3px',
    backgroundColor: getBadgeBackground(color),
    color: getBadgeColor(color),
    border: `1px solid ${getBadgeBorder(color)}`,
    transition: 'all var(--dur-fast, 0.15s) var(--ease-standard, ease)',
    cursor: 'help',
  }),
  tooltip: {
    position: 'absolute' as const,
    bottom: 'calc(100% + 8px)',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'var(--tooltip-bg, var(--card-bg))',
    color: 'var(--tooltip-text, var(--main-text))',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '12px',
    lineHeight: '1.5',
    minWidth: '220px',
    maxWidth: '320px',
    boxShadow: '0 8px 24px color-mix(in srgb, var(--main-text) 32%, transparent)',
    zIndex: 1000,
    whiteSpace: 'pre-wrap' as const,
    border: '1px solid var(--border-color)',
  },
  tooltipArrow: {
    position: 'absolute' as const,
    bottom: '-6px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: 0,
    borderLeft: '6px solid transparent',
    borderRight: '6px solid transparent',
    borderTop: '6px solid var(--tooltip-bg, var(--card-bg))',
  },
  tooltipHeader: {
    fontWeight: 600,
    marginBottom: '8px',
    paddingBottom: '8px',
    borderBottom: '1px solid var(--border-color)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '4px',
  },
  tooltipLabel: {
    color: 'var(--text-secondary, var(--main-text))',
  },
  tooltipValue: {
    color: 'var(--main-text)',
    fontWeight: 500,
  },
  recommendation: (type: string) => ({
    marginTop: '8px',
    padding: '8px',
    borderRadius: '6px',
    backgroundColor:
      type === 'approve'
        ? 'color-mix(in srgb, var(--success-color) 15%, transparent)'
        : type === 'reject'
          ? 'color-mix(in srgb, var(--error-color) 15%, transparent)'
          : 'color-mix(in srgb, var(--warning-color) 15%, transparent)',
    color:
      type === 'approve'
        ? 'var(--success-color)'
        : type === 'reject'
          ? 'var(--error-color)'
          : 'var(--warning-color)',
    fontSize: '11px',
    fontWeight: 500,
  }),
  loading: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    backgroundColor: 'color-mix(in srgb, var(--main-text) 5%, transparent)',
    color: 'var(--text-secondary, var(--main-text))',
  },
  spinner: {
    width: '10px',
    height: '10px',
    border: '2px solid color-mix(in srgb, var(--main-text) 20%, transparent)',
    borderTopColor: 'var(--main-accent, var(--quantum-violet, #6C4CFF))',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};

function getBadgeBackground(color: string): string {
  const colors: Record<string, string> = {
    green: 'color-mix(in srgb, var(--success-color) 15%, transparent)',
    yellow: 'color-mix(in srgb, var(--warning-color) 15%, transparent)',
    orange: 'color-mix(in srgb, var(--warning-color) 15%, transparent)',
    red: 'color-mix(in srgb, var(--error-color) 15%, transparent)',
    gray: 'color-mix(in srgb, var(--main-text) 5%, transparent)',
  };
  return colors[color] || colors.gray;
}

function getBadgeColor(color: string): string {
  const colors: Record<string, string> = {
    green: 'var(--success-color)',
    yellow: 'var(--warning-color)',
    orange: 'var(--warning-color)',
    red: 'var(--error-color)',
    gray: 'var(--text-secondary, var(--main-text))',
  };
  return colors[color] || colors.gray;
}

function getBadgeBorder(color: string): string {
  const colors: Record<string, string> = {
    green: 'color-mix(in srgb, var(--success-color) 30%, transparent)',
    yellow: 'color-mix(in srgb, var(--warning-color) 30%, transparent)',
    orange: 'color-mix(in srgb, var(--warning-color) 30%, transparent)',
    red: 'color-mix(in srgb, var(--error-color) 30%, transparent)',
    gray: 'var(--border-color)',
  };
  return colors[color] || colors.gray;
}

function getBadgeIcon(color: string): CdaIconName {
  const icons: Record<string, CdaIconName> = {
    green: 'shield-check',
    yellow: 'alert-triangle',
    orange: 'alert-triangle',
    red: 'shield-alert',
    gray: 'file-check',
  };
  return icons[color] || 'file-check';
}

function formatDecisionTime(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

function priorityDot(color: string, size = 8) {
  return React!.createElement('span', {
    style: {
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: '50%',
      backgroundColor: color,
      display: 'inline-block',
      flexShrink: 0,
    },
    'aria-hidden': 'true',
  });
}

export interface ApprovalInsightsBadgeProps {
  taskTitle: string;
  resourceType?: string;
  actionType?: string;
  showTooltip?: boolean;
  compact?: boolean;
  canonicalRisk?: UnifiedApprovalRisk | null;
  fetchInsights?: FetchApprovalInsights;
}

export function ApprovalInsightsBadge({
  taskTitle,
  resourceType,
  actionType,
  showTooltip = true,
  compact = false,
  canonicalRisk = null,
  fetchInsights,
}: ApprovalInsightsBadgeProps): React.ReactElement | null {
  const [insights, setInsights] = useState<ApprovalInsightsResult>(EMPTY_INSIGHTS);
  const [loading, setLoading] = useState(Boolean(fetchInsights && !canonicalRisk));
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    if (canonicalRisk || !fetchInsights) {
      setLoading(false);
      return undefined;
    }

    let mounted = true;

    async function loadInsights() {
      setLoading(true);
      try {
        const result = await fetchInsights!(taskTitle, resourceType, actionType);
        if (mounted) setInsights(result);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadInsights();
    return () => {
      mounted = false;
    };
  }, [taskTitle, resourceType, actionType, canonicalRisk, fetchInsights]);

  const canonicalBadge = useMemo(() => {
    if (!canonicalRisk) return null;
    const color = riskLevelToBadgeColor(canonicalRisk.riskLevel);
    const riskLabel = canonicalRisk.riskLevel
      ? `${canonicalRisk.riskLevel.charAt(0).toUpperCase()}${canonicalRisk.riskLevel.slice(1)} risk`
      : 'Access request';
    const similar =
      canonicalRisk.similarApprovedCount && canonicalRisk.similarApprovedCount > 0
        ? ` · ${canonicalRisk.similarApprovedCount} similar approved`
        : '';
    return {
      icon:
        canonicalRisk.riskLevel === 'critical' || canonicalRisk.riskLevel === 'high'
          ? ('shield-alert' as CdaIconName)
          : ('info' as CdaIconName),
      text: compact ? (canonicalRisk.riskLevel ?? 'risk') : `${riskLabel}${similar}`,
      color,
      tooltip: `Unified risk from access-request metadata${similar}`,
      ariaLabel: `Approval risk: ${riskLabel}${similar}`,
    };
  }, [canonicalRisk, compact]);

  const badgeContent = useMemo(() => {
    if (canonicalBadge) return canonicalBadge;
    if (loading) return { icon: 'clock' as CdaIconName, text: 'Loading...', color: 'gray', ariaLabel: 'Loading approval insights' };
    if (!insights.has_insights) return null;
    return {
      icon: getBadgeIcon(insights.badge_color),
      text: compact ? insights.stats.approval_rate_display : insights.badge_text,
      color: insights.badge_color,
      ariaLabel: `Approval insights: ${insights.badge_text}`,
    };
  }, [canonicalBadge, insights, loading, compact]);

  if (!loading && !badgeContent) return null;

  if (loading) {
    return React!.createElement(
      'span',
      {
        style: styles.loading,
        'aria-label': 'Loading approval insights',
        'data-testid': 'approval-insights-badge',
      },
      React!.createElement('span', { style: styles.spinner }),
      'Insights',
    );
  }

  if (!badgeContent) return null;

  const ariaLabel =
    'ariaLabel' in badgeContent && badgeContent.ariaLabel
      ? badgeContent.ariaLabel
      : `Approval insights: ${badgeContent.text}`;

  return React!.createElement(
    'span',
    {
      style: { ...styles.container, position: 'relative' as const },
      onMouseEnter: () => showTooltip && setShowTip(true),
      onMouseLeave: () => setShowTip(false),
      'data-testid': 'approval-insights-badge',
    },
    React!.createElement(
      'span',
      {
        style: styles.badge(badgeContent.color),
        role: 'status',
        'aria-label': ariaLabel,
      },
      React!.createElement(CdaIcon, { name: badgeContent.icon, size: 14 }),
      React!.createElement('span', null, badgeContent.text),
    ),
    showTip &&
      showTooltip &&
      (canonicalBadge
        ? React!.createElement(
            'div',
            { style: styles.tooltip, role: 'tooltip' },
            React!.createElement(
              'div',
              { style: styles.tooltipHeader },
              React!.createElement(CdaIcon, { name: 'info', size: 14 }),
              React!.createElement('span', null, 'Approval risk'),
            ),
            React!.createElement('div', null, canonicalBadge.tooltip),
            React!.createElement('div', { style: styles.tooltipArrow }),
          )
        : insights.has_insights &&
            React!.createElement(
              'div',
              { style: styles.tooltip, role: 'tooltip' },
              React!.createElement(
                'div',
                { style: styles.tooltipHeader },
                React!.createElement(CdaIcon, { name: 'file-check', size: 14 }),
                React!.createElement('span', null, 'Approval Insights'),
              ),
              React!.createElement(
                'div',
                { style: styles.tooltipRow },
                React!.createElement('span', { style: styles.tooltipLabel }, 'Similar requests:'),
                React!.createElement('span', { style: styles.tooltipValue }, insights.stats.total_similar),
              ),
              React!.createElement(
                'div',
                { style: styles.tooltipRow },
                React!.createElement('span', { style: styles.tooltipLabel }, 'Approved:'),
                React!.createElement(
                  'span',
                  { style: styles.tooltipValue },
                  `${insights.stats.approved_count} (${insights.stats.approval_rate_display})`,
                ),
              ),
              React!.createElement(
                'div',
                { style: styles.tooltipRow },
                React!.createElement('span', { style: styles.tooltipLabel }, 'Rejected:'),
                React!.createElement(
                  'span',
                  { style: styles.tooltipValue },
                  `${insights.stats.rejected_count} (${Math.round((1 - insights.stats.approval_rate) * 100)}%)`,
                ),
              ),
              insights.stats.avg_decision_time_seconds > 0 &&
                React!.createElement(
                  'div',
                  { style: styles.tooltipRow },
                  React!.createElement('span', { style: styles.tooltipLabel }, 'Avg. decision time:'),
                  React!.createElement(
                    'span',
                    { style: styles.tooltipValue },
                    formatDecisionTime(insights.stats.avg_decision_time_seconds),
                  ),
                ),
              insights.stats.confidence !== 'low' &&
                React!.createElement(
                  'div',
                  { style: styles.tooltipRow },
                  React!.createElement('span', { style: styles.tooltipLabel }, 'Confidence:'),
                  React!.createElement(
                    'span',
                    {
                      style: {
                        ...styles.tooltipValue,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                      },
                    },
                    priorityDot(
                      insights.stats.confidence === 'high'
                        ? 'var(--success-color)'
                        : 'var(--warning-color)',
                    ),
                    insights.stats.confidence === 'high' ? 'High' : 'Medium',
                  ),
                ),
              insights.recommendation_reason &&
                React!.createElement(
                  'div',
                  {
                    style: {
                      ...styles.recommendation(insights.recommendation),
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    },
                  },
                  React!.createElement(CdaIcon, { name: 'info', size: 13 }),
                  insights.recommendation_reason,
                ),
              React!.createElement('div', { style: styles.tooltipArrow }),
            )),
  );
}

export function useApprovalInsights(
  taskTitle: string,
  resourceType?: string,
  actionType?: string,
  fetchInsights?: FetchApprovalInsights,
): { insights: ApprovalInsightsResult; loading: boolean } {
  const [insights, setInsights] = useState<ApprovalInsightsResult>(EMPTY_INSIGHTS);
  const [loading, setLoading] = useState(Boolean(fetchInsights));

  useEffect(() => {
    if (!fetchInsights) {
      setLoading(false);
      return undefined;
    }

    let mounted = true;

    async function fetch() {
      setLoading(true);
      try {
        const result = await fetchInsights!(taskTitle, resourceType, actionType);
        if (mounted) setInsights(result);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetch();
    return () => {
      mounted = false;
    };
  }, [taskTitle, resourceType, actionType, fetchInsights]);

  return { insights, loading };
}

export default ApprovalInsightsBadge;
